// Minimal, dependency-free .xlsx writer.
//
// Produces a genuine Office Open XML spreadsheet as a stored (uncompressed) ZIP — no jszip,
// no SheetJS, nothing to `npm install`, so it always builds on the old CRA/webpack toolchain
// and never risks a production build break. Report exports are small tables, so skipping
// compression costs nothing meaningful.
//
//   downloadXlsx([{ name: 'Summary', rows: [['Question','Score'], ['Cleanliness', 4.21]] }], 'report.xlsx')
//
// Cell values that are finite numbers are written as numeric cells; everything else as text.

// ---- CRC32 (for ZIP entries) ----
let CRC_TABLE = null;
function crcTable() {
    if (CRC_TABLE) return CRC_TABLE;
    const t = new Uint32Array(256);
    for (let n = 0; n < 256; n++) {
        let c = n;
        for (let k = 0; k < 8; k++) c = (c & 1) ? (0xEDB88320 ^ (c >>> 1)) : (c >>> 1);
        t[n] = c >>> 0;
    }
    CRC_TABLE = t;
    return t;
}
function crc32(bytes) {
    const t = crcTable();
    let c = 0xFFFFFFFF;
    for (let i = 0; i < bytes.length; i++) c = t[(c ^ bytes[i]) & 0xFF] ^ (c >>> 8);
    return (c ^ 0xFFFFFFFF) >>> 0;
}

// ---- little-endian byte helpers ----
function u16(arr, v) { arr.push(v & 0xFF, (v >>> 8) & 0xFF); }
function u32(arr, v) { arr.push(v & 0xFF, (v >>> 8) & 0xFF, (v >>> 16) & 0xFF, (v >>> 24) & 0xFF); }
function pushBytes(arr, bytes) { for (let i = 0; i < bytes.length; i++) arr.push(bytes[i]); }

function xmlEsc(s) {
    return String(s)
        .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;').replace(/'/g, '&apos;');
}

// Excel column letter for a 0-based index (0→A, 26→AA).
function colName(i) {
    let s = '';
    i++;
    while (i > 0) { const m = (i - 1) % 26; s = String.fromCharCode(65 + m) + s; i = Math.floor((i - 1) / 26); }
    return s;
}

function sanitizeSheetName(name, fallback) {
    let n = String(name || fallback).replace(/[\[\]:*?/\\]/g, ' ').trim();
    if (!n) n = fallback;
    return n.slice(0, 31);
}

function sheetXml(rows) {
    let body = '';
    (rows || []).forEach((row, r) => {
        const rn = r + 1;
        let cells = '';
        (row || []).forEach((val, c) => {
            if (val === null || val === undefined || val === '') return;
            const ref = colName(c) + rn;
            if (typeof val === 'number' && isFinite(val)) {
                cells += `<c r="${ref}"><v>${val}</v></c>`;
            } else {
                cells += `<c r="${ref}" t="inlineStr"><is><t xml:space="preserve">${xmlEsc(val)}</t></is></c>`;
            }
        });
        body += `<row r="${rn}">${cells}</row>`;
    });
    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">'
        + `<sheetData>${body}</sheetData></worksheet>`;
}

function contentTypesXml(sheets) {
    let overrides = '<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>';
    sheets.forEach((s, i) => {
        overrides += `<Override PartName="/xl/worksheets/sheet${i + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`;
    });
    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">'
        + '<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>'
        + '<Default Extension="xml" ContentType="application/xml"/>'
        + overrides + '</Types>';
}

function rootRelsXml() {
    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
        + '<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>'
        + '</Relationships>';
}

function workbookXml(sheets) {
    let s = '';
    sheets.forEach((sh, i) => { s += `<sheet name="${xmlEsc(sanitizeSheetName(sh.name, 'Sheet' + (i + 1)))}" sheetId="${i + 1}" r:id="rId${i + 1}"/>`; });
    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">'
        + `<sheets>${s}</sheets></workbook>`;
}

function workbookRelsXml(sheets) {
    let r = '';
    sheets.forEach((sh, i) => { r += `<Relationship Id="rId${i + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${i + 1}.xml"/>`; });
    return '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>'
        + '<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">'
        + r + '</Relationships>';
}

function buildXlsx(sheets) {
    const enc = new TextEncoder();
    const files = [
        { name: '[Content_Types].xml', data: contentTypesXml(sheets) },
        { name: '_rels/.rels', data: rootRelsXml() },
        { name: 'xl/workbook.xml', data: workbookXml(sheets) },
        { name: 'xl/_rels/workbook.xml.rels', data: workbookRelsXml(sheets) },
    ];
    sheets.forEach((s, i) => files.push({ name: `xl/worksheets/sheet${i + 1}.xml`, data: sheetXml(s.rows) }));
    files.forEach(f => { f.bytes = enc.encode(f.data); f.crc = crc32(f.bytes); f.nameBytes = enc.encode(f.name); });

    const local = [];   // local file records
    const central = []; // central directory records
    files.forEach(f => {
        f.offset = local.length;
        // local file header
        u32(local, 0x04034b50); u16(local, 20); u16(local, 0); u16(local, 0); u16(local, 0); u16(local, 0x21);
        u32(local, f.crc); u32(local, f.bytes.length); u32(local, f.bytes.length);
        u16(local, f.nameBytes.length); u16(local, 0);
        pushBytes(local, f.nameBytes); pushBytes(local, f.bytes);
    });
    files.forEach(f => {
        // central directory header
        u32(central, 0x02014b50); u16(central, 20); u16(central, 20); u16(central, 0); u16(central, 0); u16(central, 0); u16(central, 0x21);
        u32(central, f.crc); u32(central, f.bytes.length); u32(central, f.bytes.length);
        u16(central, f.nameBytes.length); u16(central, 0); u16(central, 0); u16(central, 0); u16(central, 0); u32(central, 0);
        u32(central, f.offset);
        pushBytes(central, f.nameBytes);
    });

    const out = [];
    pushBytes(out, local);
    const cdStart = local.length;
    pushBytes(out, central);
    // end of central directory
    u32(out, 0x06054b50); u16(out, 0); u16(out, 0); u16(out, files.length); u16(out, files.length);
    u32(out, central.length); u32(out, cdStart); u16(out, 0);

    return new Blob([new Uint8Array(out)], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
}

function downloadXlsx(sheets, filename) {
    const blob = buildXlsx(sheets);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'export.xlsx';
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export { downloadXlsx, buildXlsx };
